'use client';

import { hasPreferencesConsent } from '@lib/analytics/consent';
import { CONSENT_UPDATED_EVENT } from '@lib/analytics/constants';
import { COUNTRY_CODE } from '@lib/constants';
import { type SearchProductResult, searchProducts } from '@lib/data/products';
import { useDebounce } from '@lib/hooks/use-debounce';
import { cn } from '@lib/util/cn';
import { sanitizeSearchQuery } from '@lib/util/sanitize-query';
import { LoadingImage } from '@modules/common/components/loading-image';
import { Search as SearchIcon, Spinner, XClose } from '@modules/common/icons';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const RECENT_SEARCHES_KEY = '_recent_searches';

const MAX_RECENT_SEARCHES = 5;

type SearchAutocompleteProps = {
  variant: 'desktop' | 'mobile';
  onNavigate?: () => void;
};

const SearchAutocomplete = ({
  variant,
  onNavigate,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Search autocomplete manages debounced queries, keyboard navigation, recent searches, and result display states
}: SearchAutocompleteProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchProductResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const debouncedQuery = useDebounce(query, 300);
  const sanitizedDebouncedQuery = sanitizeSearchQuery(debouncedQuery) ?? '';
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const showRecentSearches =
    isOpen && query.length === 0 && recentSearches.length > 0;
  const showResults =
    isOpen &&
    sanitizedDebouncedQuery.length >= 2 &&
    (results.length > 0 || isLoading);
  const showEmpty =
    isOpen &&
    sanitizedDebouncedQuery.length >= 2 &&
    !isLoading &&
    results.length === 0 &&
    query.length >= 2;
  const showDropdown = showRecentSearches || showResults || showEmpty;

  // Load recent searches from localStorage when preferences consent is granted
  useEffect(() => {
    try {
      const stored = hasPreferencesConsent()
        ? localStorage.getItem(RECENT_SEARCHES_KEY)
        : null;
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  // Clear persisted recent searches and in-memory state when preferences
  // consent is withdrawn
  useEffect(() => {
    const handleConsentUpdated = () => {
      if (!hasPreferencesConsent()) {
        localStorage.removeItem(RECENT_SEARCHES_KEY);
        setRecentSearches([]);
      }
    };

    window.addEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdated);
    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdated);
    };
  }, []);

  // Fetch search results when debounced query changes
  useEffect(() => {
    if (sanitizedDebouncedQuery.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    searchProducts({
      query: debouncedQuery,
      countryCode: COUNTRY_CODE,
      limit: 6,
    })
      .then((data) => {
        if (!cancelled) {
          setResults(data.products);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, sanitizedDebouncedQuery]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveRecentSearch = (term: string) => {
    const updated = [
      term,
      ...recentSearches.filter((s) => s.toLowerCase() !== term.toLowerCase()),
    ].slice(0, MAX_RECENT_SEARCHES);
    setRecentSearches(updated);

    if (!hasPreferencesConsent()) {
      return;
    }

    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    } catch {
      // Silently fail
    }
  };

  const navigateToProduct = (handle: string) => {
    saveRecentSearch(query);
    setIsOpen(false);
    setQuery('');
    onNavigate?.();
    router.push(`/${handle}`);
  };

  const handleSubmit = () => {
    const trimmed = query.trim();
    if (trimmed) {
      saveRecentSearch(trimmed);
      setIsOpen(false);
      setQuery('');
      onNavigate?.();
      router.push(`/search?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    let totalItems: number;
    if (showRecentSearches) {
      totalItems = recentSearches.length;
    } else if (results.length > 0) {
      totalItems = results.length + 1; // +1 for "View all results"
    } else {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (showRecentSearches && activeIndex >= 0) {
          const term = recentSearches[activeIndex];
          setQuery(term);
          setActiveIndex(-1);
        } else if (activeIndex >= 0 && activeIndex < results.length) {
          navigateToProduct(results[activeIndex].handle);
        } else {
          handleSubmit();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  const isDesktop = variant === 'desktop';

  const inputClasses = isDesktop
    ? 'h-12 pl-10 pr-10 text-base rounded-lg border-border-semantic bg-white text-fg-neutral-strong placeholder:text-brand-neutral-500 focus:outline-hidden focus:border-fg-accent-soft focus:ring-1 focus:ring-fg-accent-soft transition-width duration-300 ease-in-out w-[320px] xl:w-[400px]'
    : 'w-full h-12 pl-10 pr-10 text-base border rounded-lg border-border-semantic bg-white text-fg-neutral-strong placeholder:text-brand-neutral-500 focus:outline-hidden focus:border-fg-accent-soft focus:ring-1 focus:ring-fg-accent-soft';

  const searchIconClasses = isDesktop
    ? 'absolute left-3 top-1/2 -translate-y-1/2 text-brand-neutral-900 pointer-events-none'
    : 'absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-neutral-900 pointer-events-none';

  const clearBtnClasses = isDesktop
    ? 'absolute cursor-pointer right-3 top-1/2 -translate-y-1/2 text-brand-neutral-900 hover:text-fg-neutral-soft'
    : 'absolute right-3.5 top-1/2 -translate-y-1/2 text-brand-neutral-900 hover:text-fg-neutral-soft';

  const dropdownClasses = isDesktop
    ? 'absolute top-full right-0 mt-2 w-[380px] bg-white border border-border-semantic-subtle rounded-lg shadow-lg z-[50] overflow-hidden'
    : 'absolute top-full left-0 right-0 mt-2 bg-white border border-border-semantic-subtle rounded-lg shadow-lg z-[50] overflow-hidden';

  return (
    <div ref={containerRef} className="relative">
      <search>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="relative group"
        >
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
              setActiveIndex(-1);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search treatments, products..."
            className={inputClasses}
            role="combobox"
            aria-label="Search products"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-controls={showDropdown ? 'search-results-listbox' : undefined}
            aria-activedescendant={
              activeIndex >= 0 ? `search-result-${activeIndex}` : undefined
            }
            autoComplete="off"
          />
          <SearchIcon
            size="20"
            className={searchIconClasses}
            aria-hidden="true"
          />
          <div
            aria-hidden="true"
            className={`absolute right-0 inset-y-0 w-20 rounded-r-lg z-10 bg-linear-to-r from-transparent to-white pointer-events-none transition-opacity duration-300 group-focus-within:opacity-0 ${isDesktop ? 'block' : 'hidden'}`}
          />
          {query && (
            <button
              type="button"
              onClick={clearSearch}
              className={clearBtnClasses}
              aria-label="Clear search"
            >
              <XClose style={{ width: 18, height: 18 }} />
            </button>
          )}
        </form>
      </search>

      {showDropdown && (
        <div
          className={dropdownClasses}
          id="search-results-listbox"
          role="listbox"
          aria-label="Search suggestions"
        >
          {/* Recent searches */}
          {showRecentSearches && (
            <div className="p-3">
              <p className="text-xs font-medium text-fg-neutral-soft uppercase tracking-wide mb-2">
                Recent Searches
              </p>
              <ul>
                {recentSearches.map((term, index) => (
                  <li key={term}>
                    <button
                      type="button"
                      id={`search-result-${index}`}
                      role="option"
                      aria-selected={activeIndex === index}
                      onClick={() => {
                        setQuery(term);
                        setActiveIndex(-1);
                      }}
                      className={cn(
                        'flex items-center cursor-pointer gap-2 w-full px-2 py-1.5 text-sm rounded transition-colors',
                        activeIndex === index
                          ? 'bg-surface-neutral-subtle text-fg-neutral-strong'
                          : 'text-fg-neutral-subtle hover:bg-surface-neutral-subtle',
                      )}
                    >
                      <SearchIcon
                        size="14"
                        className="text-fg-neutral-soft shrink-0"
                        aria-hidden="true"
                      />
                      <span className="truncate">{term}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Loading */}
          {isLoading && !showRecentSearches && (
            <div
              className="flex items-center justify-center py-8"
              role="status"
              aria-label="Loading"
            >
              <Spinner
                size="20"
                className="text-fg-neutral-soft"
                aria-hidden="true"
              />
            </div>
          )}

          {/* Empty state */}
          {showEmpty && (
            <div className="py-8 text-center">
              <p className="text-sm text-fg-neutral-soft">
                No products found for &ldquo;{sanitizedDebouncedQuery}&rdquo;
              </p>
            </div>
          )}

          {/* Product results */}
          {!isLoading && results.length > 0 && !showRecentSearches && (
            <>
              <ul className="py-1">
                {results.map((product, index) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      id={`search-result-${index}`}
                      role="option"
                      aria-selected={activeIndex === index}
                      onClick={() => navigateToProduct(product.handle)}
                      className={cn(
                        'flex items-center cursor-pointer gap-3 w-full px-3 py-2 text-left transition-colors',
                        activeIndex === index
                          ? 'bg-surface-neutral-subtle'
                          : 'hover:bg-surface-neutral-subtle',
                      )}
                    >
                      <div className="w-10 h-10 shrink-0 bg-[#F3F4F3] rounded overflow-hidden">
                        <LoadingImage
                          src={product.thumbnail ?? undefined}
                          alt={product.title}
                          width={40}
                          height={40}
                          className="w-full h-full object-contain mix-blend-multiply"
                          placeholderIconSize={16}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-fg-neutral-strong truncate">
                          {product.title}
                        </p>
                        {product.calculated_price && (
                          <p className="text-xs text-fg-neutral-soft mt-0.5">
                            {product.calculated_price}
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                id={`search-result-${results.length}`}
                role="option"
                aria-selected={activeIndex === results.length}
                onClick={handleSubmit}
                className={cn(
                  'flex items-center cursor-pointer justify-center w-full px-3 py-3 text-sm font-medium border-t border-border-semantic-subtle transition-colors',
                  activeIndex === results.length
                    ? 'bg-surface-neutral-subtle text-fg-interactive-strong'
                    : 'text-fg-interactive-strong hover:bg-surface-neutral-subtle',
                )}
              >
                View all results for &ldquo;{query}&rdquo;
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
